import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	fetchCart,
	removeFromCart,
	updateItemQuantity,
	clearCart
} from "../redux/thunks/cartThunks";
import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
import { RiDeleteBin6Line } from "react-icons/ri";
import Header from "../Page/Header/Header";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import Footer from "../Components/Footer/Footer";

const CartPage = () => {
	const { items: cart, totalItems, totalPrice, loading, error } = useSelector(state => state.cart);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [couponCode, setCouponCode] = useState("");
	const [showCouponInput, setShowCouponInput] = useState(false);
	const [appliedDiscount, setAppliedDiscount] = useState(0);
	const [showShippingAddress, setShowShippingAddress] = useState(false);
	const debounceTimeout = useRef(null);

	const handleQuantityChange = async (productId, variantId, newQuantity) => {
		try {
			await dispatch(updateItemQuantity({ productId, variantId, quantity: newQuantity }));
		} catch (error) {
			console.error("Error updating quantity:", error);
			alert(error.response?.data?.message);
		}
	};

	const handleRemove = async (item) => {
		try {
			const productId = item.productId;
			const variantId = item.variantId
			console.log("prod", productId)
			console.log("item", item)
			await dispatch(removeFromCart({ productId, variantId }));
		} catch (error) {
			console.error("Error removing item:", error);
			alert("Failed to remove item from cart.");
		}
	};

	// Clean up timeout on component unmount
	useEffect(() => {
		return () => {
			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}
		};
	}, []);

	// Fetch cart when component mounts
	useEffect(() => {
		const fetchCartData = async () => {
			try {
				await dispatch(fetchCart());
			} catch (error) {
				console.error("Failed to fetch cart:", error);
			}
		};

		fetchCartData();
	}, [dispatch]);

	const applyDiscount = () => {
		if (couponCode.toUpperCase() === "DISCOUNT10") {
			const discount = totalPrice * 0.1;
			setAppliedDiscount(discount);
			alert("Discount applied successfully!");
		} else {
			alert("Invalid discount code");
		}
	};

	const handleContinue = () => {
		setShowShippingAddress(true);
	};

	const handleShippingSubmit = (address) => {
		console.log("Shipping Address:", address);
		setShowShippingAddress(false);
	};

	const renderQuantityControls = (item) => {
		return (
			<div className="flex items-center justify-center space-x-2">
				<button
					className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${item.quantity <= 1
						? "bg-gray-100 text-gray-400 cursor-not-allowed"
						: "bg-gray-200 hover:bg-gray-300"
						}`}
					onClick={(e) => {
						e.stopPropagation();
						handleQuantityChange(item.productId, item.variantId, item.quantity - 1);
					}}
					disabled={item.quantity <= 1}
				>
					<span className="text-lg">−</span>
				</button>
				<span className="w-8 text-center">
					{item.quantity}
				</span>
				<button
					className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
					onClick={(e) => {
						e.stopPropagation();
						handleQuantityChange(item.productId, item.variantId, item.quantity + 1);
					}}
				>
					<span className="text-lg">+</span>
				</button>
			</div>
		);
	};

	if (error) {
		return (
			<>
				<Header />
				<div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
					<div className="bg-white p-4 rounded-lg shadow text-center">
						<p className="text-red-500 mb-4">Error loading cart: {error}</p>
						<button
							onClick={() => dispatch(fetchCart())}
							className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
						>
							Retry
						</button>
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<Header />
			<div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
				<h1 className="text-xl md:text-2xl font-bold flex items-center">
					<span className="text-xl text-blue-500 mr-2">🛒</span> Cart
					{totalItems > 0 && (
						<span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
							{totalItems}
						</span>
					)}
				</h1>
				<div className="mt-2 mb-4 flex items-start gap-2 text-sm text-gray-700">
					{cart.length === 1 ? (
						<p>
							<strong>Fast Delivery:</strong> For one product, delivery will be within
							<strong className="text-gray-900"> 2 hours</strong>.
						</p>
					) : cart.length > 1 ? (
						<p>
							<strong>Delivery Time:</strong> For multiple products, delivery may take
							<strong className="text-gray-900"> more than 2 hours</strong>.
						</p>
					) : (
						<p className="text-gray-400">Add products to see delivery time</p>
					)}
				</div>

				<div className="flex flex-col lg:flex-row gap-4 md:gap-8">
					{/* Cart Items Table */}
					<div className="lg:w-2/3">
						<div className="bg-white rounded-lg shadow overflow-hidden">
							{cart.length === 0 ? (
								<div className="text-center py-12">
									<p className="text-gray-500 text-lg">Your cart is empty</p>
								</div>
							) : (
								<>
									{/* Desktop Table */}
									<div className="hidden md:block">
										<table className="w-full">
											<thead className="bg-gray-50">
												<tr>
													<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Product
													</th>
													<th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
														Quantity
													</th>
													<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
														Subtotal
													</th>
													<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
														Actions
													</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-gray-200">
												{cart.map((item) => (
													<tr key={`${item.productId._id}-${item.variantId || 'no-variant'}`}>
														<td className="px-6 py-4">
															<div className="flex items-center">
																<div className="flex-shrink-0 h-20 w-20">
																	<img
																		className="h-20 w-20 object-cover rounded cursor-pointer"
																		src={item.image || '../../../assets/brand-default.jpg'}
																		alt={item.name}
																		onClick={() => navigate(`/product/${item.slug}`)}
																	/>
																</div>

																<div className="ml-4 min-w-0">
																	<div className="group relative">
																		<div className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:line-clamp-none">
																			{item.name}
																		</div>
																	</div>


																	{item.variantName && (
																		<div className="text-sm text-gray-500 truncate">
																			Variant: {item.variantName}
																		</div>
																	)}

																	<div className="text-sm text-gray-500">
																		{item.price.toFixed(2)} Rs
																	</div>
																</div>
															</div>
														</td>

														<td className="px-6 py-4 whitespace-nowrap">
															{renderQuantityControls(item)}
														</td>

														<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
															{(item.price * item.quantity).toFixed(2)} Rs
														</td>

														<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
															<button
																onClick={() => handleRemove(item)}
																className="text-red-600 hover:text-red-900 transition-colors"
															>
																<span className="text-lg">
																	<RiDeleteBin6Line />
																</span>
															</button>
														</td>
													</tr>

												))}
											</tbody>
										</table>
									</div>
									<div className="md:hidden">
										{cart.map((item) => (
											<div key={`${item.productId._id}-${item.variantId || 'no-variant'}`} className="p-4 border-b border-gray-200">
												<div className="flex justify-between items-start">
													<div className="flex items-center">
														<div className="flex-shrink-0 h-16 w-16">
															<img
																className="h-16 w-16 object-cover rounded"
																src={item.image || '../../../assets/brand-default.jpg'}
																alt={item.name}
															/>
														</div>
														<div className="ml-3">
															<div className="text-sm font-medium text-gray-900">
																{item.name}
															</div>
															{item.variantName && (
																<div className="text-sm text-gray-500">
																	Variant: {item.variantName}
																</div>
															)}
															<div className="text-sm text-gray-500">
																{item.price.toFixed(2)} Rs
															</div>
														</div>
													</div>
													<button
														onClick={() => handleRemove(item)}
														className="text-red-600 hover:text-red-900 transition-colors"
													>
														<RiDeleteBin6Line size={18} />
													</button>
												</div>

												<div className="mt-3 flex justify-between items-center">
													{renderQuantityControls(item)}
													<div className="text-sm font-medium">
														Subtotal: {(item.price * item.quantity).toFixed(2)} Rs
													</div>
												</div>
											</div>
										))}
									</div>
								</>
							)}
						</div>
					</div>

					{/* Order Summary */}
					<div className="lg:w-1/3">
						<div className="bg-white rounded-lg shadow p-4 md:p-6">
							<h2 className="text-lg font-semibold mb-4">Order Summary</h2>
							<div className="space-y-2 mb-4">
								<div className="flex justify-between text-sm">
									<span>Subtotal</span>
									<span>{totalPrice.toFixed(2)} Rs</span>
								</div>
								{/* <div className="flex justify-between text-sm">
                  <span>Shipping Charges</span>
                  <span>{cart.length > 0 ? 2.5 : 0} Rs</span>
                </div> */}
								{/* {appliedDiscount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-{appliedDiscount.toFixed(2)} Rs</span>
                  </div>
                )} */}
								<div className="flex justify-between font-semibold text-lg pt-2 border-t">
									<span>Total</span>
									<span>{totalPrice} Rs</span>
								</div>
							</div>

							<button
								className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${cart.length === 0
									? "bg-gray-400 text-gray-200 cursor-not-allowed"
									: "bg-blue-500 text-white hover:bg-blue-600"
									}`}
								onClick={handleContinue}
								disabled={cart.length === 0}
							>
								Continue
							</button>

							{/* <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="useDiscount"
                  className="mr-2"
                  checked={showCouponInput}
                  onChange={() => setShowCouponInput(!showCouponInput)}
                  disabled={cart.length === 0}
                />
                <label 
                  htmlFor="useDiscount" 
                  className={`text-sm ${cart.length === 0 ? "text-gray-400" : "text-gray-600"}`}
                >
                  Use discount code
                </label>
              </div> */}

							{/* {showCouponInput && (
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Enter code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={applyDiscount}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
                  >
                    Apply
                  </button>
                </div>
              )} */}
						</div>
					</div>
				</div>


				{showShippingAddress && (
					<ShippingAddress
						onClose={() => setShowShippingAddress(false)}
						onSubmit={handleShippingSubmit}
						orderSummary={{
							subtotal: totalPrice,
							shippingCharges: cart.length > 0 ? 2.5 : 0,
							taxes: cart.length > 0 ? totalPrice * 0.05 : 0,
							appliedDiscount,
							total: totalPrice + (cart.length > 0 ? 2.5 : 0) - appliedDiscount,
						}}
						clearCart={() => dispatch(clearCart())}
						fetchCart={() => dispatch(fetchCart())}
					/>
				)}
			</div>
			<Footer />
		</>
	);
};

export default CartPage;
